import axios from "axios";

const MAX_PART_RETRIES = 3;
const MAX_SIGNED_PARTS_PER_REQUEST = 20;
const s3Transport = axios.create({
  withCredentials: false,
  withXSRFToken: false,
});

const createSemaphore = (limit) => {
  let active = 0;
  const waiting = [];

  const drain = () => {
    while (active < limit && waiting.length) {
      const entry = waiting.shift();
      if (entry.settled) continue;
      entry.settled = true;
      entry.signal?.removeEventListener("abort", entry.cancel);
      active += 1;
      entry.resolve(() => {
        active -= 1;
        drain();
      });
    }
  };

  return async (operation, signal) => {
    const release = await new Promise((resolve, reject) => {
      const entry = {
        resolve,
        reject,
        signal,
        settled: false,
        cancel: null,
      };
      entry.cancel = () => {
        if (entry.settled) return;
        entry.settled = true;
        const index = waiting.indexOf(entry);
        if (index >= 0) waiting.splice(index, 1);
        reject(new axios.CanceledError("Upload cancelled"));
      };
      if (signal?.aborted) {
        entry.cancel();
        return;
      }
      signal?.addEventListener("abort", entry.cancel, { once: true });
      waiting.push(entry);
      drain();
    });
    try {
      return await operation();
    } finally {
      release();
    }
  };
};

// Shared across all upload surfaces so simultaneous components cannot exceed
// the EC2/S3 concurrency budget.
const withFileSlot = createSemaphore(3);
const withPartSlot = createSemaphore(6);
const uploadControllers = new Map();

const emitUploadState = (detail) => {
  window.dispatchEvent(new CustomEvent("pdf-upload-state", { detail }));
};

window.addEventListener("pdf-upload-cancel", (event) => {
  const controller = uploadControllers.get(event.detail?.uploadToken);
  if (controller) controller.abort();
});

const isPdf = (file) =>
  file instanceof File &&
  (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"));

const asBoolean = (value) =>
  value === true || value === "true" || value === "True" || value === "1";

const parseUploadRequest = (url, formData) => {
  let requestUrl;
  try {
    requestUrl = new URL(url, window.location.origin);
  } catch {
    return null;
  }

  const singleMatch = requestUrl.pathname.match(/\/api\/upload_doc\/(\d+)\/(\d+)\/?$/);
  const isBatchEndpoint = /\/api\/upload\/doc\/?$/.test(requestUrl.pathname);
  if (!singleMatch && !isBatchEndpoint) return null;

  const files = [];
  for (const [key, value] of formData.entries()) {
    if (value instanceof File && (key === "file" || /^file\d+$/.test(key))) {
      files.push({ key, file: value });
    }
  }
  if (!files.some(({ file }) => isPdf(file))) return null;

  return {
    files,
    isBatchEndpoint,
    clientId: singleMatch ? singleMatch[1] : formData.get("client_id"),
    caseId: singleMatch ? singleMatch[2] : formData.get("case_id"),
    isInbox:
      isBatchEndpoint || asBoolean(formData.get("isInbox")) || asBoolean(formData.get("is_inbox")),
  };
};

const buildLegacyFormData = (source, files, isBatchEndpoint) => {
  const legacy = new FormData();
  for (const [key, value] of source.entries()) {
    if (key === "file" || /^file\d+$/.test(key) || key === "number_of_files") {
      continue;
    }
    legacy.append(key, value);
  }

  files.forEach(({ file }, index) => {
    legacy.append(isBatchEndpoint ? `file${index}` : "file", file);
  });
  if (isBatchEndpoint) legacy.append("number_of_files", String(files.length));
  return legacy;
};

const splitIntoChunks = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const delay = (milliseconds, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new axios.CanceledError("Upload cancelled"));
      return;
    }
    const abort = () => {
      window.clearTimeout(timer);
      reject(new axios.CanceledError("Upload cancelled"));
    };
    const timer = window.setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, milliseconds);
    signal?.addEventListener("abort", abort, { once: true });
  });

const syntheticResponse = (originalResponses, directResults, config) => {
  const legacyIds = originalResponses.flatMap((response) => {
    const responseIds = response?.data?.docIds;
    if (Array.isArray(responseIds)) return responseIds;
    return response?.data?.docId ? [response.data.docId] : [];
  });
  const directIds = directResults.map((result) => result.docId).filter(Boolean);
  const docIds = [...legacyIds, ...directIds];
  const lastResponse = originalResponses.at(-1);

  return {
    data: {
      ...(lastResponse?.data || {}),
      response: "success",
      docId: docIds.at(-1) || null,
      docIds,
      uploads: directResults,
    },
    status: directResults.length ? 201 : lastResponse?.status || 200,
    statusText: "Created",
    headers: lastResponse?.headers || {},
    config,
    request: lastResponse?.request,
  };
};

export const installPdfMultipartUpload = (api) => {
  const authenticatedPost = api.post.bind(api);
  const authenticatedGet = api.get.bind(api);
  const authenticatedDelete = api.delete.bind(api);

  api.post = async (url, data, config = {}) => {
    if (!(data instanceof FormData)) {
      return authenticatedPost(url, data, config);
    }

    const request = parseUploadRequest(url, data);
    if (!request) return authenticatedPost(url, data, config);
    if (!request.clientId || !request.caseId) {
      return authenticatedPost(url, data, config);
    }

    const pdfFiles = request.files.filter(({ file }) => isPdf(file));
    const legacyFiles = request.files.filter(({ file }) => !isPdf(file));
    const totalBytes = request.files.reduce((total, item) => total + item.file.size, 0);
    const loadedByFile = new Map();

    const reportAggregateProgress = (uploadToken, file, loaded, state = {}) => {
      loadedByFile.set(uploadToken, Math.min(loaded, file.size));
      const aggregateLoaded = Array.from(loadedByFile.values()).reduce(
        (total, value) => total + value,
        0
      );
      config.onUploadProgress?.({
        loaded: aggregateLoaded,
        total: totalBytes,
        progress: totalBytes ? aggregateLoaded / totalBytes : 0,
      });
      const detail = {
        uploadToken,
        fileName: file.name,
        fileSize: file.size,
        loaded: Math.min(loaded, file.size),
        progress: file.size ? Math.round((Math.min(loaded, file.size) * 100) / file.size) : 0,
        ...state,
      };
      config.onFileUploadProgress?.(detail);
      emitUploadState(detail);
    };

    const uploadPdf = async ({ file }, fileIndex) =>
      withFileSlot(async () => {
        const controller = new AbortController();
        const uploadToken = `${Date.now()}-${fileIndex}-${Math.random().toString(36).slice(2)}`;
        uploadControllers.set(uploadToken, controller);
        const abortFromCaller = () => controller.abort();
        config.signal?.addEventListener("abort", abortFromCaller, { once: true });
        let sessionId = null;

        try {
          reportAggregateProgress(uploadToken, file, 0, { status: "initiating" });
          const initiated = await authenticatedPost(
            "/api/document-uploads/initiate/",
            {
              file_name: file.name,
              content_type: file.type || "application/pdf",
              size: file.size,
              client_id: request.clientId,
              case_id: request.caseId,
              is_inbox: request.isInbox,
            },
            { signal: controller.signal }
          );
          sessionId = initiated.data.session_id;
          const partSize = initiated.data.part_size;
          const partNumbers = Array.from(
            { length: initiated.data.part_count },
            (_, index) => index + 1
          );
          const signedUrls = new Map();

          for (const partBatch of splitIntoChunks(partNumbers, MAX_SIGNED_PARTS_PER_REQUEST)) {
            const signed = await authenticatedPost(
              `/api/document-uploads/${sessionId}/parts/`,
              { part_numbers: partBatch },
              { signal: controller.signal }
            );
            signed.data.parts.forEach((part) => {
              signedUrls.set(part.part_number, part.url);
            });
          }

          const loadedByPart = new Map();
          const completedParts = await Promise.all(
            partNumbers.map((partNumber) =>
              withPartSlot(async () => {
                const start = (partNumber - 1) * partSize;
                const end = Math.min(start + partSize, file.size);
                const blob = file.slice(start, end);
                let signedUrl = signedUrls.get(partNumber);

                for (let attempt = 0; attempt <= MAX_PART_RETRIES; attempt += 1) {
                  try {
                    const response = await s3Transport.put(signedUrl, blob, {
                      headers: { "Content-Type": "application/octet-stream" },
                      withCredentials: false,
                      signal: controller.signal,
                      onUploadProgress: (event) => {
                        loadedByPart.set(partNumber, Math.min(event.loaded || 0, blob.size));
                        const fileLoaded = Array.from(loadedByPart.values()).reduce(
                          (total, value) => total + value,
                          0
                        );
                        reportAggregateProgress(uploadToken, file, fileLoaded, {
                          status: "uploading",
                          partNumber,
                          attempt: attempt + 1,
                        });
                      },
                    });
                    const etag = response.headers?.etag;
                    if (!etag) {
                      throw new Error(
                        "S3 did not expose the ETag response header. Check bucket CORS."
                      );
                    }
                    loadedByPart.set(partNumber, blob.size);
                    return { part_number: partNumber, etag };
                  } catch (error) {
                    if (axios.isCancel(error) || controller.signal.aborted) throw error;
                    if (attempt >= MAX_PART_RETRIES) throw error;

                    const retryDetail = {
                      uploadToken,
                      fileName: file.name,
                      status: "retrying",
                      partNumber,
                      attempt: attempt + 2,
                      error: error.message,
                    };
                    config.onFileUploadRetry?.(retryDetail);
                    emitUploadState(retryDetail);

                    if (error.response?.status === 403) {
                      const refreshed = await authenticatedPost(
                        `/api/document-uploads/${sessionId}/parts/`,
                        { part_numbers: [partNumber] },
                        { signal: controller.signal }
                      );
                      signedUrl = refreshed.data.parts[0].url;
                    }
                    await delay(500 * 2 ** attempt, controller.signal);
                  }
                }
                throw new Error(`Unable to upload part ${partNumber}.`);
              }, controller.signal)
            )
          );

          reportAggregateProgress(uploadToken, file, file.size, {
            status: "finalizing",
          });
          const completionPayload = {
            upload_id: initiated.data.upload_id,
            parts: completedParts.sort((left, right) => left.part_number - right.part_number),
          };
          let completed = null;
          let completionError = null;
          for (let attempt = 0; attempt <= MAX_PART_RETRIES; attempt += 1) {
            try {
              completed = await authenticatedPost(
                `/api/document-uploads/${sessionId}/complete/`,
                completionPayload,
                { signal: controller.signal }
              );
              break;
            } catch (error) {
              if (axios.isCancel(error) || controller.signal.aborted) throw error;
              completionError = error;
              try {
                const current = await authenticatedGet(`/api/document-uploads/${sessionId}/`);
                if (current.data.docId) {
                  completed = {
                    ...current,
                    data: { response: "success", ...current.data },
                  };
                  break;
                }
              } catch {
                // The completion POST remains the authoritative retry path.
              }
              if (attempt < MAX_PART_RETRIES) {
                await delay(500 * 2 ** attempt, controller.signal);
              }
            }
          }
          if (!completed) throw completionError || new Error("Unable to finalize upload.");
          reportAggregateProgress(uploadToken, file, file.size, { status: "completed" });
          return completed.data;
        } catch (error) {
          const cancelled = axios.isCancel(error) || controller.signal.aborted;
          const detail = {
            uploadToken,
            fileName: file.name,
            fileSize: file.size,
            status: cancelled ? "cancelled" : "failed",
            error: error.message,
          };
          if (cancelled) config.onFileUploadCancelled?.(detail);
          else config.onFileUploadError?.(detail);
          emitUploadState(detail);

          if (sessionId) {
            try {
              await authenticatedDelete(`/api/document-uploads/${sessionId}/`);
            } catch {
              // The 24-hour session expiry and S3 lifecycle rule are the fallback.
            }
          }
          throw error;
        } finally {
          config.signal?.removeEventListener("abort", abortFromCaller);
          uploadControllers.delete(uploadToken);
        }
      }, config.signal);

    const directSettledPromise = Promise.allSettled(
      pdfFiles.map((entry, index) => uploadPdf(entry, index))
    );
    const originalResponses = [];

    if (legacyFiles.length) {
      if (request.isBatchEndpoint) {
        const legacyData = buildLegacyFormData(data, legacyFiles, true);
        originalResponses.push(await authenticatedPost(url, legacyData, config));
      } else {
        for (const legacyFile of legacyFiles) {
          const legacyData = buildLegacyFormData(data, [legacyFile], false);
          originalResponses.push(await authenticatedPost(url, legacyData, config));
        }
      }
    }

    const directSettled = await directSettledPromise;
    const failed = directSettled.find((result) => result.status === "rejected");
    if (failed) throw failed.reason;
    return syntheticResponse(
      originalResponses,
      directSettled.map((result) => result.value),
      config
    );
  };
};
